import React from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Check,
  Sparkles,
  Zap,
  ChevronRight,
  Lock,
  Crown,
  Star,
  Shield,
  Bot,
  BarChart3,
  Mail,
  Users,
  TrendingUp,
} from "lucide-react";

const Paywall = ({ isOpen, onClose, featureName = "this feature" }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate("/payment");
  };

  const proFeatures = [
    {
      icon: <Bot className="h-5 w-5" />,
      text: "25 AI Requests/day",
      color: "indigo",
    },
    {
      icon: <Zap className="h-5 w-5" />,
      text: "12 Quiz Slots",
      color: "green",
    },
    {
      icon: <Sparkles className="h-5 w-5" />,
      text: "Full Vault Access",
      color: "purple",
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      text: "Advanced Analytics",
      color: "yellow",
    },
    { icon: <Mail className="h-5 w-5" />, text: "Email Prizes", color: "pink" },
    {
      icon: <Users className="h-5 w-5" />,
      text: "10 Sessions per Quiz",
      color: "blue",
    },
    {
      icon: <TrendingUp className="h-5 w-5" />,
      text: "Priority Support",
      color: "orange",
    },
  ];

  const colorClasses = {
    indigo: "bg-indigo-100 text-indigo-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    yellow: "bg-yellow-100 text-yellow-600",
    pink: "bg-pink-100 text-pink-600",
    blue: "bg-blue-100 text-blue-600",
    orange: "bg-orange-100 text-orange-600",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6 sm:p-8 text-white overflow-hidden flex-shrink-0">
          {/* Pattern Overlay */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-all p-2 hover:bg-white/20 rounded-lg z-10 backdrop-blur-sm"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Branding Section */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/20 shadow-lg">
                <div className="relative">
                  <div className="absolute -inset-1 rounded-full bg-white/20 blur opacity-50"></div>
                  <img
                    src="/QemplateFull.png"
                    alt="Qemplate Logo"
                    className="relative h-6 w-auto z-10"
                  />
                </div>
                <div className="h-5 w-px bg-white/30"></div>
                <span className="text-sm font-semibold">Pro</span>
              </div>

              <div className="hidden sm:block bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/20 shadow-lg">
                <Crown className="h-7 w-7" />
              </div>
            </div>

            {/* Title Section */}
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold mb-1">
                Upgrade to Pro
              </h2>
              <p className="text-indigo-100 text-sm sm:text-base">
                Access {featureName} and additional features
              </p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 sm:p-8 overflow-y-auto flex-1">
          {/* Tier Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Starter Tier */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200 p-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Starter
                </h3>
                <p className="text-gray-600 text-sm">Free plan</p>
              </div>
              <ul className="space-y-3">
                {[
                  "2 Quiz Slots",
                  "View-only Vault",
                  "3 AI Requests/day",
                  "1 Session per Quiz",
                  "Basic Analytics",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
                <li className="flex items-center gap-2 pt-2 border-t border-gray-200">
                  <X className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm text-gray-400 line-through">
                    Email Prizes
                  </span>
                </li>
              </ul>
            </div>

            {/* Pro Tier */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-300 p-6 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg">
                  Pro plan
                </span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Pro</h3>
                <p className="text-gray-600 text-sm">
                  <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    £4.99
                  </span>
                  <span className="text-gray-500">/month</span>
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  "12 Quiz Slots",
                  "Full Vault Access",
                  "25 AI Requests/day",
                  "10 Sessions per Quiz",
                  "Advanced Analytics",
                  "Email Prizes",
                  "Priority Support",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                    <span className="text-sm text-gray-900 font-medium">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pro Features Grid */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" fill="currentColor" />
              Pro plan features
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {proFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center text-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`${
                      colorClasses[feature.color]
                    } rounded-lg p-2 mb-2`}
                  >
                    {feature.icon}
                  </div>
                  <p className="text-xs text-gray-700 font-medium">
                    {feature.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-100">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              About the Pro plan
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="bg-indigo-100 rounded-lg p-2 flex-shrink-0">
                  <Zap className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">
                    Higher limits
                  </h5>
                  <p className="text-sm text-gray-700">
                    Increased quiz slots and daily AI requests compared to the free plan
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-lg p-2 flex-shrink-0">
                  <Lock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">
                    Additional access
                  </h5>
                  <p className="text-sm text-gray-700">
                    Add quizzes from the vault and access additional features
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleUpgrade}
              className="flex-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-semibold py-4 px-6 rounded-xl hover:shadow-2xl hover:shadow-purple-500/50 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Crown className="h-5 w-5" />
                <span>Subscribe - £4.99/month</span>
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="px-6 py-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
            >
              Maybe Later
            </button>
          </div>

          {/* Security Note */}
          <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-100">
            <Shield className="h-4 w-4 text-gray-400" />
            <p className="text-xs text-gray-500">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
      `}</style>
    </div>
  );
};

export default Paywall;
